import { regex } from 'arkregex';
import {
  ALPHANUMERIC_CHAR_PATTERN,
  BOUNDARY_CHARS_SOURCE,
  LEADING_PUNCTUATION_PATTERN,
  stripHandlePrefixes,
  TRAILING_BRACKET_PATTERN,
  USER_HANDLE_PART_SOURCE,
} from '@/lib/utils/regex';

interface ConvertOptions {
  sampleTweetIds?: string[];
}

function escapeHtmlText(value: string): string {
  if (!value) {
    return '';
  }
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

function escapeHtmlAttribute(value: string): string {
  if (!value) {
    return '';
  }
  return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;');
}

function buildUserAnchor(username: string, linkText: string): string {
  const normalized = stripHandlePrefixes(username).trim();
  if (!normalized) {
    return linkText;
  }
  const url = `https://x.com/${normalized}`;
  const safeText = escapeHtmlText(linkText || `@${normalized}`);
  return `<a href="${url}" data-username="${normalized}" data-preview="user" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">${safeText}</a>`;
}

function buildTweetAnchor(tweetId: string, linkText: string): string {
  const url = `https://x.com/i/status/${tweetId}`;
  const normalizedText = linkText?.trim();
  const fallbackText = 'tweet ↗';
  const safeText = escapeHtmlText(normalizedText && normalizedText.length > 0 ? normalizedText : fallbackText);
  return `<a href="${url}" data-tweet-id="${tweetId}" data-preview="tweet" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">${safeText}</a>`;
}

const TWEET_ID_SOURCE = String.raw`\d[\d_]*`;
const LONG_TWEET_ID_SOURCE = String.raw`\d[\d_]{9,}`;
const SHORT_TWEET_ID_SOURCE = String.raw`\d[\d_]{4,}`;
const TWEET_ID_CAPTURE = `(${TWEET_ID_SOURCE})`;
const LONG_TWEET_ID_CAPTURE = `(${LONG_TWEET_ID_SOURCE})`;
const SHORT_TWEET_ID_CAPTURE = `(${SHORT_TWEET_ID_SOURCE})`;
const USER_HANDLE_PART = USER_HANDLE_PART_SOURCE;
const TWEET_CONTEXT_PREFIX = String.raw`(?:include|includes|including|like|such\s+as|featuring|covering|highlighting|spotlighting|profiling|detailing|discussing|examining|exploring|referencing|touching\s+on|focusing\s+on|focus\s+on|focuses\s+on|focused\s+on|centering\s+on|centres\s+on|centered\s+on|dive\s+into|dives\s+into|diving\s+into|delve\s+into|delves\s+into|delving\s+into|looking\s+at|looks\s+at|addressing|unpacking)`;
const CONJUNCTION_PREFIX = '(?:and|or)';

const INLINE_TWEET_REF = regex.as(String.raw`\[([^\]]+)\|ID\s*:${TWEET_ID_CAPTURE}\]`, 'gi');
const INLINE_TWEET_ALT = regex.as(String.raw`\[([^\]]+)\]\|ID\s*:${LONG_TWEET_ID_CAPTURE}`, 'gi');
const KEYWORD_TWEET_REF = regex.as(
  String.raw`(\b${TWEET_CONTEXT_PREFIX}\s+)([^|]+?)\|ID\s*:${LONG_TWEET_ID_CAPTURE}`,
  'gi'
);
const CONJUNCTION_TWEET_REF = regex.as(
  String.raw`(\b${CONJUNCTION_PREFIX}\s+)([^|]+?)\|ID\s*:${LONG_TWEET_ID_CAPTURE}`,
  'gi'
);
const BOUNDARY_TWEET_REF = regex.as(
  String.raw`(^|\s|${BOUNDARY_CHARS_SOURCE})([^|]+?)\|ID\s*:${LONG_TWEET_ID_CAPTURE}`,
  'gi'
);
const BRACKETED_ID = regex.as(String.raw`\[\s*ID\s*:${TWEET_ID_CAPTURE}\s*\]`, 'gi');
const INLINE_TWEET_LINK = regex.as(String.raw`\[([^\]]+)\]\(\s*tweet:${SHORT_TWEET_ID_CAPTURE}\s*\)`, 'gi');
const SUGGESTION_LINK = regex.as(String.raw`\[([^\]]+)\]\(\s*suggest:\/\/([^\)\s]+)(?:\s+"[^"\\]*")?\s*\)`, 'gi');
const PART_LABEL_TWEET = regex.as(String.raw`(Part\s+\d+[-\w/'"”]*)\s+ID\s*:${LONG_TWEET_ID_CAPTURE}`, 'gi');
const INLINE_ID_FALLBACK = regex.as(String.raw`\bID\s*:${LONG_TWEET_ID_CAPTURE}\b`, 'gi');
const USER_BLOCK = regex.as(String.raw`\[([^|\]]+)\|USER\s*: ?\s*@?(${USER_HANDLE_PART})\]`, 'gi');
const USER_INLINE = regex.as(String.raw`\[\s*USER\s*: ?\s*@?(${USER_HANDLE_PART})\s*\]`, 'gi');
const USER_LINK = regex.as(String.raw`\[([^\]]+)\]\(\s*@(${USER_HANDLE_PART})\s*\)`, 'gi');
const VIEW_TWEET_FALLBACK = regex.as(String.raw`\[([^\]]+)\]\(\s*(?:view\s*tweet|tweet\s*link|tweet)\s*\)`, 'gi');
const GENERIC_LINK = regex.as(String.raw`\[([^\]]+)\]\s*\(\s*(https?:\/\/[\S]+?)(?:\s+"[^"\\]*")?\s*\)`, 'gi');
const UNBOUND_TWEET_ID = regex.as(String.raw`(?<![A-Za-z0-9/_\-:"'])${LONG_TWEET_ID_CAPTURE}(?![A-Za-z0-9_-])`, 'g');
const TWEET_TAG = regex.as(String.raw`<tweet:\s*${SHORT_TWEET_ID_CAPTURE}\s*>`, 'gi');
const INLINE_TWEET_TOKEN = regex.as(`tweet:${SHORT_TWEET_ID_CAPTURE}`, 'gi');
const MENTION_PATTERN = regex.as(String.raw`(^|\s)@(${USER_HANDLE_PART})\b`, 'g');

export function convertTweetReferences(text: string, options: ConvertOptions = {}): string {
  if (typeof text !== 'string') {
    return String(text);
  }

  let out = text;
  const sampleIds = Array.isArray(options.sampleTweetIds) ? [...options.sampleTweetIds] : [];
  const consumeSampleId = (): string | null => {
    if (sampleIds.length === 0) {
      return null;
    }
    const next = sampleIds.shift();
    return typeof next === 'string' && next.trim().length > 0 ? next.trim() : null;
  };

  out = out.replaceAll(INLINE_TWEET_REF, (_m, linkText: string, tweetId: string) =>
    buildTweetAnchor(tweetId, linkText)
  );

  out = out.replaceAll(INLINE_TWEET_ALT, (_m, linkText: string, tweetId: string) =>
    buildTweetAnchor(tweetId, linkText)
  );

  out = out.replaceAll(KEYWORD_TWEET_REF, (_m, prefix: string, rawLabel: string, tweetId: string) => {
    const trimmedLabel = rawLabel.trim().replace(TRAILING_BRACKET_PATTERN, '');
    const fallbackLabel = trimmedLabel.length > 0 ? trimmedLabel : `ID:${tweetId}`;
    return `${prefix}${buildTweetAnchor(tweetId, fallbackLabel)}`;
  });

  out = out.replaceAll(CONJUNCTION_TWEET_REF, (_m, prefix: string, rawLabel: string, tweetId: string) => {
    const trimmedLabel = rawLabel.trim().replace(TRAILING_BRACKET_PATTERN, '');
    const fallbackLabel = trimmedLabel.length > 0 ? trimmedLabel : `ID:${tweetId}`;
    return `${prefix}${buildTweetAnchor(tweetId, fallbackLabel)}`;
  });

  out = out.replaceAll(BOUNDARY_TWEET_REF, (_m, boundary: string, rawLabel: string, tweetId: string) => {
    const trimmedLabel = rawLabel.trim().replace(LEADING_PUNCTUATION_PATTERN, '').replace(TRAILING_BRACKET_PATTERN, '');
    const fallbackLabel = trimmedLabel.length > 0 ? trimmedLabel : `ID:${tweetId}`;
    if (!boundary) {
      return buildTweetAnchor(tweetId, fallbackLabel);
    }
    return `${boundary}${buildTweetAnchor(tweetId, fallbackLabel)}`;
  });

  out = out.replaceAll(BRACKETED_ID, (_m, tweetId: string) => buildTweetAnchor(tweetId, 'this tweet'));

  out = out.replaceAll(INLINE_TWEET_LINK, (_m, linkText: string, tweetId: string) =>
    buildTweetAnchor(tweetId, linkText)
  );

  out = out.replaceAll(SUGGESTION_LINK, (_m, linkText: string, suggestion: string) => {
    const safeText = escapeHtmlText(linkText);
    const safeTarget = escapeHtmlAttribute(`suggest://${suggestion}`);
    return `<a href="${safeTarget}">${safeText}</a>`;
  });

  out = out.replaceAll(PART_LABEL_TWEET, (_m, label: string, tweetId: string) => buildTweetAnchor(tweetId, label));

  out = out.replaceAll(INLINE_ID_FALLBACK, (_m, tweetId: string) => buildTweetAnchor(tweetId, `ID:${tweetId}`));

  out = out.replaceAll(USER_BLOCK, (_m, linkText: string, username: string) => buildUserAnchor(username, linkText));

  out = out.replaceAll(USER_INLINE, (_m, username: string) => {
    const normalized = stripHandlePrefixes(username);
    const label = normalized ? `@${normalized}` : username;
    return buildUserAnchor(username, label);
  });

  out = out.replaceAll(USER_LINK, (_m, linkText: string, username: string) => {
    const normalized = stripHandlePrefixes(username).trim();
    if (normalized.length === 0) {
      return linkText;
    }
    return buildUserAnchor(normalized, linkText);
  });

  out = out.replaceAll(VIEW_TWEET_FALLBACK, (_m, linkText: string) => {
    const tweetId = consumeSampleId();
    if (tweetId == null || tweetId.length === 0) {
      return linkText;
    }
    return buildTweetAnchor(tweetId, linkText);
  });

  out = out.replaceAll(GENERIC_LINK, (_match, linkText: string, url: string) => {
    const safeText = escapeHtmlText(linkText);
    const safeUrl = escapeHtmlAttribute(url.trim());
    return `<a href="${safeUrl}">${safeText}</a>`;
  });

  out = out.replaceAll(UNBOUND_TWEET_ID, (_m, tweetId: string) => buildTweetAnchor(tweetId, tweetId));

  out = out.replaceAll(TWEET_TAG, (_m: string, tweetId: string) => buildTweetAnchor(tweetId, ''));

  out = out.replaceAll(INLINE_TWEET_TOKEN, (fullMatch: string, tweetId: string, offset: number, source: string) => {
    if (tweetId === undefined) {
      return fullMatch;
    }
    const previousChar = offset > 0 ? source.charAt(offset - 1) : '';
    if (previousChar === '>' || previousChar === '/' || ALPHANUMERIC_CHAR_PATTERN.test(previousChar)) {
      return fullMatch;
    }
    const nextChar = offset + fullMatch.length < source.length ? source.charAt(offset + fullMatch.length) : '';
    if (nextChar === '<') {
      return fullMatch;
    }
    return buildTweetAnchor(tweetId, '');
  });

  return out;
}

export function convertTweetReferencesToMarkdown(text: string, options: ConvertOptions = {}): string {
  if (typeof text !== 'string') {
    return String(text);
  }
  let out = text;
  out = out.replaceAll(INLINE_TWEET_REF, (_m, linkText: string, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[${linkText}](${url})`;
  });
  out = out.replaceAll(INLINE_TWEET_ALT, (_m, linkText: string, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[${linkText}](${url})`;
  });
  out = out.replaceAll(KEYWORD_TWEET_REF, (_m, prefix: string, rawLabel: string, tweetId: string) => {
    const trimmedLabel = rawLabel.trim().replace(TRAILING_BRACKET_PATTERN, '');
    const label = trimmedLabel.length > 0 ? trimmedLabel : `ID:${tweetId}`;
    const url = `https://x.com/i/status/${tweetId}`;
    return `${prefix}[${label}](${url})`;
  });
  out = out.replaceAll(CONJUNCTION_TWEET_REF, (_m, prefix: string, rawLabel: string, tweetId: string) => {
    const trimmedLabel = rawLabel.trim().replace(TRAILING_BRACKET_PATTERN, '');
    const label = trimmedLabel.length > 0 ? trimmedLabel : `ID:${tweetId}`;
    const url = `https://x.com/i/status/${tweetId}`;
    return `${prefix}[${label}](${url})`;
  });
  out = out.replaceAll(BOUNDARY_TWEET_REF, (_m, boundary: string, rawLabel: string, tweetId: string) => {
    const trimmedLabel = rawLabel.trim().replace(LEADING_PUNCTUATION_PATTERN, '').replace(TRAILING_BRACKET_PATTERN, '');
    const label = trimmedLabel.length > 0 ? trimmedLabel : `ID:${tweetId}`;
    const url = `https://x.com/i/status/${tweetId}`;
    if (!boundary) {
      return `[${label}](${url})`;
    }
    return `${boundary}[${label}](${url})`;
  });
  out = out.replaceAll(BRACKETED_ID, (_m, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[this tweet](${url})`;
  });

  out = out.replaceAll(INLINE_TWEET_LINK, (_m, linkText: string, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[${linkText}](${url})`;
  });

  out = out.replaceAll(
    SUGGESTION_LINK,
    (_m, linkText: string, suggestion: string) => `[${linkText}](suggest://${suggestion})`
  );
  out = out.replaceAll(GENERIC_LINK, (_match, linkText: string, url: string) => {
    const normalizedUrl = url.trim();
    return `[${linkText}](${normalizedUrl})`;
  });
  out = out.replaceAll(PART_LABEL_TWEET, (_m, label: string, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[${label}](${url})`;
  });
  out = out.replaceAll(INLINE_ID_FALLBACK, (_m, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[ID:${tweetId}](${url})`;
  });
  out = out.replaceAll(USER_BLOCK, (_m, linkText: string, username: string) => {
    const normalized = stripHandlePrefixes(username).trim();
    if (!normalized) {
      return linkText;
    }
    return `[${linkText}](https://x.com/${normalized})`;
  });
  out = out.replaceAll(USER_INLINE, (_m, username: string) => {
    const normalized = stripHandlePrefixes(username).trim();
    const label = normalized ? `@${normalized}` : username;
    return `[${label}](https://x.com/${normalized || username})`;
  });
  out = out.replaceAll(USER_LINK, (_m, linkText: string, username: string) => {
    const normalized = stripHandlePrefixes(username).trim();
    if (!normalized) {
      return linkText;
    }
    return `[${linkText}](https://x.com/${normalized})`;
  });
  out = out.replaceAll(UNBOUND_TWEET_ID, (_m, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[${tweetId}](${url})`;
  });

  out = out.replaceAll(TWEET_TAG, (_m: string, tweetId: string) => {
    const url = `https://x.com/i/status/${tweetId}`;
    return `[tweet ↗](${url})`;
  });

  out = out.replaceAll(INLINE_TWEET_TOKEN, (fullMatch: string, tweetId: string, offset: number, source: string) => {
    if (tweetId === undefined) {
      return fullMatch;
    }
    const previousChar = offset > 0 ? source.charAt(offset - 1) : '';
    if (previousChar === '>' || previousChar === '/' || ALPHANUMERIC_CHAR_PATTERN.test(previousChar)) {
      return fullMatch;
    }
    const nextChar = offset + fullMatch.length < source.length ? source.charAt(offset + fullMatch.length) : '';
    if (nextChar === '<') {
      return fullMatch;
    }
    const url = `https://x.com/i/status/${tweetId}`;
    return `[tweet ↗](${url})`;
  });

  const sampleIds = Array.isArray(options.sampleTweetIds) ? [...options.sampleTweetIds] : [];
  out = out.replaceAll(VIEW_TWEET_FALLBACK, (_m, linkText: string) => {
    if (sampleIds.length === 0) {
      return linkText;
    }
    const next = sampleIds.shift();
    if (!next) {
      return linkText;
    }
    return `[${linkText}](https://x.com/i/status/${next})`;
  });

  return out;
}

export function linkifyMentionsToHtml(text: string): string {
  return text.replaceAll(MENTION_PATTERN, (_match, prefix: string, username: string) => {
    const normalized = username.trim();
    if (!normalized) {
      return `${prefix}@${username}`;
    }
    return `${prefix}${buildUserAnchor(normalized, `@${normalized}`)}`;
  });
}

export function buildStreamingMarkdownText(text: string, options: ConvertOptions = {}): string {
  const withMentions = linkifyMentionsToHtml(text);
  return convertTweetReferences(withMentions, options);
}

export type { ConvertOptions };
